/**
 * MyReferencesCard — Feb 2026
 * ============================
 *
 * Dashboard tile that surfaces the LAST 3 items the teen tapped Save on
 * from the in-chat AvA Reference panel (see `AvAContextDisplay.jsx`).
 * Reads from `GET /api/chat/context/mine` (defined in
 * `/app/backend/app/api/chat_context.py`).
 *
 * A "See All →" action routes to `/my-references` for the full library.
 * Empty state points the teen back to Practice so they know how items
 * end up here in the first place.
 *
 * Follows the standard `.card` + `.card-header` + `.card-title` visual
 * pattern of RecentConversationsCard so the masonry rebalances cleanly.
 */
import React, { useEffect, useState } from 'react';
import { Loader2, ExternalLink, ImageIcon, BookMarked } from 'lucide-react';
import { apiClient } from '../../api/client';

const hostnameOf = (url) => {
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return url; }
};

const MyReferencesCard = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiClient.request('/api/chat/context/mine');
        if (!cancelled) setItems((data?.items || []).slice(0, 3));
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Could not load your library.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="card" data-testid="my-references-card">
        <div className="card-header">
          <h2 className="card-title">My References</h2>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card" data-testid="my-references-card">
        <div className="card-header">
          <h2 className="card-title">My References</h2>
        </div>
        <p className="text-sm text-red-500 py-4">Failed to load references</p>
      </div>
    );
  }

  return (
    <div className="card" data-testid="my-references-card">
      <div className="card-header">
        <h2 className="card-title">My References</h2>
        <a href="/my-references" className="card-action" data-testid="my-references-see-all">
          See All →
        </a>
      </div>

      {items.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <BookMarked size={20} className="text-purple-400 mx-auto" />
          </div>
          <p className="empty-state-text">Nothing saved yet</p>
          <p className="text-xs text-gray-600 mt-2">
            Tap the <b>Save</b> icon in a chat to keep any link or image an AvA shares.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((it) => (
            <a
              key={it.id}
              href={it.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-purple-50 transition-colors"
              data-testid={`my-references-item-${it.id}`}
            >
              {it.kind === 'image' ? (
                <img
                  src={it.url}
                  alt=""
                  className="w-11 h-11 rounded-lg object-cover flex-shrink-0 bg-gray-100 border border-gray-200"
                  loading="lazy"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              ) : (
                <div
                  className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: '#EDE9FE', color: '#5B21B6' }}
                >
                  <ExternalLink size={16} />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  {it.kind === 'image'
                    ? <ImageIcon size={11} className="text-purple-500" />
                    : <ExternalLink size={11} className="text-purple-500" />}
                  <span className="text-[10px] font-bold uppercase tracking-wider text-purple-700 truncate">
                    {hostnameOf(it.url)}
                  </span>
                </div>
                <p className="text-xs text-gray-700 truncate">
                  {it.ava_name ? `Shared by ${it.ava_name}` : 'From your chat'}
                </p>
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyReferencesCard;
